import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";

import { api } from "../api";
import { dd, pct, timeAgo, tone } from "../format";
import { Card, EmptyState } from "../shell/ui";
import { MarketTag } from "./MarketTag";
import { SortableTable } from "./SortableTable";
import { AuthorChip } from "./social";

interface SavedRun {
  id: string;
  title: string | null;
  strategy_name: string;
  symbol: string;
  timeframe: string;
  market_type: string;
  leverage: number;
  total_return_pct: number | null;
  max_drawdown_pct: number | null;
  win_rate_pct: number | null;
  total_trades: number | null;
  saved_at: string;
  author_username: string;
  author_name: string;
  author_avatar: string;
  author_simulated: boolean;
}

/** Backtests → under your own runs: the ones you pressed Save on, yours or anyone's. */
export function SavedBacktests() {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["saved-runs"],
    queryFn: () => api<{ items: SavedRun[] }>("/hub/me/saved-runs").then((r) => r.items),
  });
  return (
    <Card title="Saved backtests" flush>
      <SortableTable
        tableId="saved-runs"
        rows={q.data ?? []}
        loading={q.isPending}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/r/${r.id}`)}
        initialSort={{ key: "saved", direction: "desc" }}
        empty={
          <EmptyState
            icon="☆"
            title="No saved backtests yet"
            action={
              <Link className="btn btn--ghost" to="/benchmarks">
                Browse benchmarks
              </Link>
            }
          >
            Press Save on any backtest to keep it here, next to your own.
          </EmptyState>
        }
        columns={[
          {
            key: "title",
            label: "Backtest",
            render: (r) => (
              <Link to={`/r/${r.id}`} onClick={(e) => e.stopPropagation()}>
                <strong>{r.title ?? r.strategy_name}</strong>
              </Link>
            ),
            sortValue: (r) => r.title ?? r.strategy_name,
            firstDirection: "asc",
          },
          {
            key: "by",
            label: "By",
            render: (r) => (
              <AuthorChip
                username={r.author_username}
                name={r.author_name}
                avatar={r.author_avatar}
                simulated={r.author_simulated}
              />
            ),
            sortValue: (r) => r.author_name,
            firstDirection: "asc",
          },
          {
            key: "market",
            label: "Market",
            render: (r) => (
              <span className="mono small">
                {r.symbol} {r.timeframe} <MarketTag marketType={r.market_type} leverage={r.leverage} />
              </span>
            ),
            sortValue: (r) => `${r.symbol} ${r.timeframe}`,
            firstDirection: "asc",
          },
          {
            key: "ret",
            label: "Return",
            align: "right",
            render: (r) => <span className={tone(r.total_return_pct)}>{pct(r.total_return_pct)}</span>,
            sortValue: (r) => r.total_return_pct,
          },
          {
            key: "dd",
            label: "Max DD",
            align: "right",
            render: (r) => dd(r.max_drawdown_pct, 1),
            sortValue: (r) => r.max_drawdown_pct,
            firstDirection: "asc",
          },
          {
            key: "win",
            label: "Win rate",
            align: "right",
            render: (r) => pct(r.win_rate_pct, 0, false),
            sortValue: (r) => r.win_rate_pct,
          },
          {
            key: "trades",
            label: "Trades",
            align: "right",
            render: (r) => r.total_trades ?? "—",
            sortValue: (r) => r.total_trades,
          },
          {
            key: "saved",
            label: "Saved",
            align: "right",
            render: (r) => <span className="mono small">{timeAgo(r.saved_at)}</span>,
            sortValue: (r) => r.saved_at,
          },
        ]}
      />
    </Card>
  );
}
